import React, { useState, useEffect } from 'react';
import { TrendingUp, AlertTriangle, CheckCircle, Users, RefreshCw, ChevronDown, ChevronUp, Minus, Plus } from 'lucide-react';

const BuyBox = ({ miraklConnectionId }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState(null);
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [filter, setFilter] = useState('all');
  const [updatingPrice, setUpdatingPrice] = useState(null);
  const [customPriceProduct, setCustomPriceProduct] = useState(null);
  const [customPriceValue, setCustomPriceValue] = useState('');

  const fetchLiveProducts = async () => {
    try {
      const response = await fetch(`${window.location.hostname === 'localhost' ? 'http://localhost:3000' : 'https://syncbridge.co.za/api'}/mirakl/connections/${miraklConnectionId}/offers`);
      const data = await response.json();
      return data.offers || [];
    } catch (err) {
      console.error('Failed to fetch live products:', err);
      return [];
    }
  };

  const checkCompetitors = async (productSkus) => {
    try {
      const response = await fetch(`${window.location.hostname === 'localhost' ? 'http://localhost:3000' : 'https://syncbridge.co.za/api'}/mirakl/connections/${miraklConnectionId}/competitors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productSkus }),
      });
      const data = await response.json();
      return data.products || [];
    } catch (err) {
      console.error('Failed to check competitors:', err);
      return [];
    }
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const liveProducts = await fetchLiveProducts();
      
      if (liveProducts.length === 0) {
        setProducts([]);
        setLoading(false);
        return;
      }

      // Get product SKUs to check competitors (use product_sku, not shop_sku)
      const productSkus = liveProducts.map(p => p.product_sku).filter(Boolean);
      
      if (productSkus.length === 0) {
        setProducts(liveProducts.map(p => ({ ...p, competitors: [], hasBuyBox: true })));
        setLoading(false);
        return;
      }

      setChecking(true);
      const competitorData = await checkCompetitors(productSkus);
      
      const enrichedProducts = liveProducts.map(product => {
        const competitorInfo = competitorData.find(c => c.product_sku === product.product_sku);
        // Backend already filters out our own offers by shop_id
        const competitors = competitorInfo?.offers || [];
        
        // Get cheapest competitor by price (not total_price which includes shipping)
        const cheapestCompetitor = competitors.length > 0 
          ? competitors.reduce((min, c) => {
              const cPrice = parseFloat(c.price || c.total_price);
              const minPrice = parseFloat(min.price || min.total_price);
              return cPrice < minPrice ? c : min;
            }, competitors[0])
          : null;
        
        // Use price (not total_price which includes shipping)
        const ourPrice = parseFloat(product.price);
        const competitorPrice = cheapestCompetitor 
          ? parseFloat(cheapestCompetitor.price || cheapestCompetitor.total_price) 
          : null;
        const hasBuyBox = !competitorPrice || ourPrice <= competitorPrice;
        
        return {
          ...product,
          competitors,
          cheapestCompetitor,
          hasBuyBox,
          priceDifference: competitorPrice ? (ourPrice - competitorPrice) : 0,
        };
      });

      setProducts(enrichedProducts);
    } catch (err) {
      setError('Failed to load Buy Box data');
      console.error(err);
    } finally {
      setLoading(false);
      setChecking(false);
    }
  };

  useEffect(() => {
    if (miraklConnectionId) {
      loadData();
    }
  }, [miraklConnectionId]);

  const formatPrice = (price) => {
    return `R ${parseFloat(price || 0).toFixed(2)}`;
  };

  const updatePrice = async (product, newPrice) => {
    setUpdatingPrice(product.shop_sku);
    try {
      await fetch(`${window.location.hostname === 'localhost' ? 'http://localhost:3000' : 'https://syncbridge.co.za/api'}/mirakl/connections/${miraklConnectionId}/offers/${product.shop_sku}/price`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          price: newPrice,
          quantity: product.quantity || 1,
          stateCode: product.state_code || '11',
        }),
      });
      await loadData();
    } catch (err) {
      console.error('Failed to update price:', err);
      alert('Failed to update price');
    } finally {
      setUpdatingPrice(null);
      setCustomPriceProduct(null);
      setCustomPriceValue('');
    }
  };

  const beatCompetitor = (product) => {
    if (!product.cheapestCompetitor) return;
    // Use price field, not total_price
    const competitorPrice = parseFloat(product.cheapestCompetitor.price || product.cheapestCompetitor.total_price);
    const newPrice = competitorPrice - 1;
    updatePrice(product, newPrice);
  };

  const adjustPrice = (product, amount) => {
    const currentPrice = parseFloat(product.price);
    const newPrice = Math.max(1, currentPrice + amount);
    updatePrice(product, newPrice);
  };

  const handleCustomPriceSubmit = (product) => {
    const newPrice = parseFloat(customPriceValue);
    if (isNaN(newPrice) || newPrice <= 0) {
      alert('Please enter a valid price');
      return;
    }
    updatePrice(product, newPrice);
  };

  const productsWithCompetitors = products.filter(p => p.competitors?.length > 0);
  const productsLosingBuyBox = products.filter(p => !p.hasBuyBox && p.competitors?.length > 0);

  const filteredProducts = products.filter(p => {
    if (filter === 'winning') return p.hasBuyBox;
    if (filter === 'losing') return !p.hasBuyBox && p.competitors?.length > 0;
    return true;
  });

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>
          <div style={styles.spinner} />
          <p>{checking ? 'Checking competitors...' : 'Loading products...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Buy Box</h1>
          <p style={styles.subtitle}>Monitor competitor pricing and protect your listings</p>
        </div>
        <button style={styles.refreshBtn} onClick={loadData} disabled={loading}>
          <RefreshCw size={18} />
          Refresh
        </button>
      </div>

      {error && (
        <div style={styles.errorBanner}>
          <AlertTriangle size={18} />
          {error}
        </div>
      )}

      <div style={styles.filterTabs}>
        <button
          style={filter === 'all' ? {...styles.filterTab, ...styles.filterTabActive} : styles.filterTab}
          onClick={() => setFilter('all')}
        >
          All ({products.length})
        </button>
        <button
          style={filter === 'winning' ? {...styles.filterTab, ...styles.filterTabActive} : styles.filterTab}
          onClick={() => setFilter('winning')}
        >
          Winning ({products.filter(p => p.hasBuyBox).length})
        </button>
        <button
          style={filter === 'losing' ? {...styles.filterTab, ...styles.filterTabActive, backgroundColor: '#ffebee', color: '#d32f2f'} : styles.filterTab}
          onClick={() => setFilter('losing')}
        >
          Losing ({productsLosingBuyBox.length})
        </button>
      </div>

      <div style={styles.statsRow}>
        <div style={styles.statCard}>
          <div style={styles.statIcon}>
            <TrendingUp size={24} color="#007aff" />
          </div>
          <div>
            <p style={styles.statValue}>{products.length}</p>
            <p style={styles.statLabel}>Total Products</p>
          </div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statIcon, backgroundColor: '#fff3e0' }}>
            <Users size={24} color="#f57c00" />
          </div>
          <div>
            <p style={styles.statValue}>{productsWithCompetitors.length}</p>
            <p style={styles.statLabel}>With Competitors</p>
          </div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statIcon, backgroundColor: productsLosingBuyBox.length > 0 ? '#ffebee' : '#e8f5e9' }}>
            {productsLosingBuyBox.length > 0 ? (
              <AlertTriangle size={24} color="#d32f2f" />
            ) : (
              <CheckCircle size={24} color="#388e3c" />
            )}
          </div>
          <div>
            <p style={{ ...styles.statValue, color: productsLosingBuyBox.length > 0 ? '#d32f2f' : '#388e3c' }}>
              {productsLosingBuyBox.length}
            </p>
            <p style={styles.statLabel}>Losing Buy Box</p>
          </div>
        </div>
      </div>

      {products.length === 0 ? (
        <div style={styles.emptyState}>
          <TrendingUp size={48} color="#ccc" />
          <p>No live products found</p>
          <p style={styles.emptySubtext}>Publish products to Leroy Merlin to monitor competition</p>
        </div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Product</th>
                <th style={styles.th}>Our Price</th>
                <th style={styles.th}>Competitors</th>
                <th style={styles.th}>Cheapest Competitor</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((product) => (
                <React.Fragment key={product.offer_id || product.shop_sku}>
                  <tr 
                    style={{
                      ...styles.tr,
                      backgroundColor: !product.hasBuyBox ? '#fff5f5' : 'transparent',
                    }}
                  >
                    <td style={styles.td}>
                      <div 
                        style={styles.productCell}
                        onClick={() => product.competitors?.length > 0 && setExpandedProduct(
                          expandedProduct === product.shop_sku ? null : product.shop_sku
                        )}
                      >
                        {product.competitors?.length > 0 && (
                          expandedProduct === product.shop_sku 
                            ? <ChevronUp size={16} color="#666" style={{cursor: 'pointer'}} />
                            : <ChevronDown size={16} color="#666" style={{cursor: 'pointer'}} />
                        )}
                        <div>
                          <p style={styles.productTitle}>{product.product_title || product.shop_sku}</p>
                          <p style={styles.productSku}>SKU: {product.shop_sku}</p>
                        </div>
                      </div>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.price}>{formatPrice(product.price)}</span>
                    </td>
                    <td style={styles.td}>
                      {product.competitors?.length > 0 ? (
                        <span style={styles.competitorBadge}>
                          {product.competitors.length} seller{product.competitors.length > 1 ? 's' : ''}
                        </span>
                      ) : (
                        <span style={styles.noCompetitors}>None</span>
                      )}
                    </td>
                    <td style={styles.td}>
                      {product.cheapestCompetitor ? (
                        <div>
                          <p style={styles.competitorName}>{product.cheapestCompetitor.shop_name}</p>
                          <p style={{
                            ...styles.competitorPrice,
                            color: parseFloat(product.cheapestCompetitor.price || product.cheapestCompetitor.total_price) < parseFloat(product.price) ? '#d32f2f' : '#388e3c'
                          }}>
                            {formatPrice(product.cheapestCompetitor.price || product.cheapestCompetitor.total_price)}
                          </p>
                        </div>
                      ) : (
                        <span style={styles.noCompetitors}>-</span>
                      )}
                    </td>
                    <td style={styles.td}>
                      {product.hasBuyBox ? (
                        <span style={styles.statusWinning}>
                          <CheckCircle size={16} />
                          Winning
                        </span>
                      ) : (
                        <span style={styles.statusLosing}>
                          <AlertTriangle size={16} />
                          Losing
                        </span>
                      )}
                    </td>
                    <td style={styles.td}>
                      {updatingPrice === product.shop_sku ? (
                        <span style={styles.updating}>Updating...</span>
                      ) : customPriceProduct === product.shop_sku ? (
                        <div style={styles.customPriceForm}>
                          <input
                            type="number"
                            style={styles.customPriceInput}
                            placeholder="New price"
                            value={customPriceValue}
                            onChange={(e) => setCustomPriceValue(e.target.value)}
                            autoFocus
                          />
                          <button 
                            style={styles.customPriceBtn}
                            onClick={() => handleCustomPriceSubmit(product)}
                          >
                            Set
                          </button>
                          <button 
                            style={styles.customPriceCancelBtn}
                            onClick={() => { setCustomPriceProduct(null); setCustomPriceValue(''); }}
                          >
                            âœ•
                          </button>
                        </div>
                      ) : (
                        <div style={styles.actionButtons}>
                          <button
                            style={styles.actionBtn}
                            onClick={() => adjustPrice(product, -1)}
                            title="Decrease by R1"
                          >
                            <Minus size={14} />
                            R1
                          </button>
                          <button
                            style={styles.actionBtn}
                            onClick={() => adjustPrice(product, 1)}
                            title="Increase by R1"
                          >
                            <Plus size={14} />
                            R1
                          </button>
                          <button
                            style={styles.actionBtnSecondary}
                            onClick={() => setCustomPriceProduct(product.shop_sku)}
                            title="Set custom price"
                          >
                            Custom
                          </button>
                          {!product.hasBuyBox && product.cheapestCompetitor && (
                            <button
                              style={styles.beatBtn}
                              onClick={() => beatCompetitor(product)}
                              title="Beat competitor by R1"
                            >
                              Beat by R1
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                  {expandedProduct === product.shop_sku && product.competitors?.length > 0 && (
                    <tr>
                      <td colSpan={6} style={styles.expandedRow}>
                        <div style={styles.competitorList}>
                          <p style={styles.competitorListTitle}>All Competitors:</p>
                          {product.competitors.map((comp, idx) => (
                            <div 
                              key={idx} 
                              style={{
                                ...styles.competitorItem,
                                backgroundColor: parseFloat(comp.price || comp.total_price) < parseFloat(product.price) ? '#ffebee' : '#f5f5f5'
                              }}
                            >
                              <span style={styles.compName}>{comp.shop_name}</span>
                              <span style={styles.compGrade}>Rating: {comp.shop_grade?.toFixed(1) || 'N/A'}</span>
                              <span style={styles.compStock}>Stock: {comp.quantity}</span>
                              <span style={{
                                ...styles.compPrice,
                                color: parseFloat(comp.price || comp.total_price) < parseFloat(product.price) ? '#d32f2f' : '#388e3c'
                              }}>
                                {formatPrice(comp.price || comp.total_price)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: '0',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1d1d1f',
    margin: 0,
  },
  subtitle: {
    fontSize: '14px',
    color: '#6e6e73',
    marginTop: '4px',
  },
  refreshBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    backgroundColor: '#007aff',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px',
    color: '#6e6e73',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '3px solid #e5e5e5',
    borderTopColor: '#007aff',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '12px',
  },
  errorBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
    backgroundColor: '#ffebee',
    color: '#d32f2f',
    borderRadius: '8px',
    marginBottom: '20px',
  },
  filterTabs: {
    display: 'flex',
    gap: '8px',
    marginBottom: '20px',
  },
  filterTab: {
    padding: '10px 20px',
    backgroundColor: '#f5f5f7',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#6e6e73',
    cursor: 'pointer',
  },
  filterTabActive: {
    backgroundColor: '#007aff',
    color: '#fff',
  },
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px',
    marginBottom: '24px',
  },
  statCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '20px',
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  statIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    backgroundColor: '#e3f2fd',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  statValue: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1d1d1f',
    margin: 0,
  },
  statLabel: {
    fontSize: '13px',
    color: '#6e6e73',
    margin: 0,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px',
    backgroundColor: '#fff',
    borderRadius: '12px',
    color: '#6e6e73',
  },
  emptySubtext: {
    fontSize: '13px',
    color: '#8e8e93',
    marginTop: '4px',
  },
  tableContainer: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: '14px 16px',
    fontSize: '12px',
    fontWeight: '600',
    color: '#6e6e73',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: '1px solid #e5e5e5',
    backgroundColor: '#fafafa',
  },
  tr: {
    borderBottom: '1px solid #f0f0f0',
  },
  td: {
    padding: '14px 16px',
    fontSize: '14px',
    color: '#1d1d1f',
    verticalAlign: 'middle',
  },
  productCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
  },
  productTitle: {
    margin: 0,
    fontWeight: '500',
  },
  productSku: {
    margin: 0,
    fontSize: '12px',
    color: '#8e8e93',
  },
  price: {
    fontWeight: '600',
  },
  competitorBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    backgroundColor: '#fff3e0',
    color: '#f57c00',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '500',
  },
  noCompetitors: {
    color: '#8e8e93',
  },
  competitorName: {
    margin: 0,
    fontSize: '13px',
    fontWeight: '500',
  },
  competitorPrice: {
    margin: 0,
    fontSize: '14px',
    fontWeight: '600',
  },
  statusWinning: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    backgroundColor: '#e8f5e9',
    color: '#388e3c',
    borderRadius: '16px',
    fontSize: '13px',
    fontWeight: '500',
  },
  statusLosing: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    backgroundColor: '#ffebee',
    color: '#d32f2f',
    borderRadius: '16px',
    fontSize: '13px',
    fontWeight: '500',
  },
  actionButtons: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
  },
  actionBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '6px 10px',
    backgroundColor: '#f5f5f7',
    border: 'none',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '500',
    color: '#1d1d1f',
    cursor: 'pointer',
  },
  actionBtnSecondary: {
    padding: '6px 10px',
    backgroundColor: '#e3f2fd',
    border: 'none',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '500',
    color: '#1976d2',
    cursor: 'pointer',
  },
  beatBtn: {
    padding: '6px 10px',
    backgroundColor: '#388e3c',
    border: 'none',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '500',
    color: '#fff',
    cursor: 'pointer',
  },
  updating: {
    fontSize: '12px',
    color: '#6e6e73',
    fontStyle: 'italic',
  },
  customPriceForm: {
    display: 'flex',
    gap: '6px',
    alignItems: 'center',
  },
  customPriceInput: {
    width: '80px',
    padding: '6px 8px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '13px',
  },
  customPriceBtn: {
    padding: '6px 12px',
    backgroundColor: '#007aff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '500',
    color: '#fff',
    cursor: 'pointer',
  },
  customPriceCancelBtn: {
    padding: '6px 8px',
    backgroundColor: '#f5f5f7',
    border: 'none',
    borderRadius: '6px',
    fontSize: '12px',
    color: '#6e6e73',
    cursor: 'pointer',
  },
  expandedRow: {
    padding: '0',
    backgroundColor: '#fafafa',
  },
  competitorList: {
    padding: '16px 16px 16px 48px',
  },
  competitorListTitle: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#6e6e73',
    textTransform: 'uppercase',
    marginBottom: '12px',
  },
  competitorItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
    padding: '10px 14px',
    borderRadius: '8px',
    marginBottom: '8px',
  },
  compName: {
    flex: 1,
    fontWeight: '500',
    fontSize: '13px',
  },
  compGrade: {
    fontSize: '12px',
    color: '#6e6e73',
  },
  compStock: {
    fontSize: '12px',
    color: '#6e6e73',
  },
  compPrice: {
    fontWeight: '600',
    fontSize: '14px',
  },
};

export default BuyBox;
